/**
 * USB Device IDs for PalmSens devices
 * Ported from PalmSens.Core.Instruments.Connections.UsbDeviceId
 */

export type UsbDeviceId = {
    vendorId: number;
    productId: number;
};

// FTDI-based devices
export const QuadFTDI: UsbDeviceId = { vendorId: 0x0403, productId: 0x6011 };
export const BridgeFTDI: UsbDeviceId = { vendorId: 0x0403, productId: 0x6015 };

// Legacy PalmSens devices
export const PalmSens: UsbDeviceId = { vendorId: 0x0403, productId: 0xd180 };
export const EmStat: UsbDeviceId = { vendorId: 0x0403, productId: 0xd181 };
export const PalmSens4: UsbDeviceId = { vendorId: 0x0403, productId: 0xd182 };

// EmStat4 series (USB-CDC)
export const EmStat4LR: UsbDeviceId = { vendorId: 0x300a, productId: 0x0000 };
export const EmStat4HR: UsbDeviceId = { vendorId: 0x300a, productId: 0x0001 };
export const MultiEmStat4LR: UsbDeviceId = { vendorId: 0x300a, productId: 0x0006 };
export const MultiEmStat4HR: UsbDeviceId = { vendorId: 0x300a, productId: 0x0007 };
export const Nexus: UsbDeviceId = { vendorId: 0x300a, productId: 0x0009 };
export const EmStat4T: UsbDeviceId = { vendorId: 0x300a, productId: 0x000b };

// Third-party devices
export const Ivium: UsbDeviceId = { vendorId: 0x0403, productId: 0xf440 };

/**
 * All known device IDs
 */
export const ALL_DEVICE_IDS: Array<UsbDeviceId> = [
    QuadFTDI,
    BridgeFTDI,
    PalmSens,
    EmStat,
    PalmSens4,
    EmStat4LR,
    EmStat4HR,
    MultiEmStat4LR,
    MultiEmStat4HR,
    Nexus,
    EmStat4T,
    Ivium,
];

/**
 * Pico devices that use 230400 baud rate
 * EmStat Pico uses BridgeFTDI (0x0403:0x6015)
 * EmStat3 (legacy) also uses 230400
 */
export const PICO_DEVICE_IDS: Array<UsbDeviceId> = [
    BridgeFTDI, // EmStat Pico
    EmStat, // EmStat3 (legacy, uses 230400)
];

/**
 * Check if two device IDs match
 */
export function matchesDeviceId(device: UsbDeviceId, target: UsbDeviceId): boolean {
    return device.vendorId === target.vendorId && device.productId === target.productId;
}

/**
 * Check if device is a known PalmSens device
 */
export function isKnownDevice(device: UsbDeviceId): boolean {
    return ALL_DEVICE_IDS.some((known) => matchesDeviceId(device, known));
}

/**
 * Check if device is an EmStat4 series (USB-CDC) device
 */
export function isEmStat4Series(device: UsbDeviceId): boolean {
    return device.vendorId === 0x300a;
}

/**
 * Check if device is FTDI-based
 */
export function isFtdiDevice(device: UsbDeviceId): boolean {
    return device.vendorId === 0x0403;
}

/**
 * Check if device is a Pico device by VID/PID
 */
export function isPicoDeviceByVidPid(device: UsbDeviceId): boolean {
    return PICO_DEVICE_IDS.some((known) => matchesDeviceId(device, known));
}
